import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserCertificate } from 'src/app/model/user-certificate.model';
import { HttpService } from 'library-explorer';
import { environment } from 'src/environments/environment';
import { LanguageService } from 'src/app/services/language.service';
import { ProfileService, FileSystemService } from 'library-explorer';
import { SettingsService } from '@app/services/settings.service';
import { CertificatePreviewModalComponent } from '@app/shared/components/certificate-preview-modal/certificate-preview-modal.component';
import { DownloadService } from './api/download.service';

@Injectable({
  providedIn: 'root'
})
export class CertificateService {
  private readonly LINKEDIN_SHARE_URL = 'https://www.linkedin.com/profile/add';

  constructor(
    private readonly translateService: TranslateService,
    private readonly httpService: HttpService,
    private readonly http: HttpClient,
    private readonly profileService: ProfileService,
    private readonly languageService: LanguageService,
    private readonly settings: SettingsService,
    private readonly dialog: MatDialog,
    private readonly downloadService: DownloadService,
    private fileSystemService: FileSystemService
  ) {
  }


  public getCertificateImage(contentId: string, certificateId: string, userId: string): Observable<string> {
    return this.getCertificateToken(contentId, certificateId, userId).pipe(
      map(({ token }) => this.getCertificateImageByToken(token))
    );
  }

  public getCertificateImageByToken(token: string): string {
    return `${environment.apiUrl}api/user-certificate/${token}`;
  }

  public getCertificateToken(contentId: string, certificateId: string, userId: string): Observable<{ token: string }> {
    return this.httpService.post<{ token: string }>('get-certificate-token', { contentId, certificateId, userId });
  }

  public downloadCerfiticate(item: UserCertificate): Observable<void> {
    const headers = new HttpHeaders().set('Accept', 'application/pdf');
    let title = item.title;
    const lang = this.translateService.currentLang;
    const apiUrl = lang !== 'en' ? `${environment.apiUrl}${lang}/api/` : `${environment.apiUrl}api/`;
    let params = new HttpParams()
      .set('userId', this.profileService.getCurrentProfileValue()?.id)
      .set('certificateId', item.id)
      .set('apiUrl', apiUrl)
      .set('language', this.languageService.currentLang || 'en');

    if (item.customCertificate) {
      title = item.customCertificate.title;
      params = params.set('customCertificateId', item.customCertificate.id);
    }

    return this.http.get(`${environment.renderingToolUrl}/certificate`, {
      params,
      headers: this.httpService.getTokenHeader(headers),
      responseType: 'blob',
    }).pipe(
      map((cert) => {
        this.downloadService.download(cert, `${title}.pdf`);
      })
    );
  }

  public shareCertificateOnLinkedIn(certificate: UserCertificate): void {
    const userId = this.profileService.getCurrentProfileValue()?.id;

    forkJoin([
      this.settings.getSettings(),
      this.getCertificateToken(certificate.id, certificate.customCertificate.id, userId),
    ])
      .subscribe(([data, { token }]) => {
        const url = new URL(this.LINKEDIN_SHARE_URL);
        const certificateUrl = `${environment.frontBaseDomain}/user-certificate/${token}`;
        const dateOfCompletion = certificate.dateOfCompletion ? new Date(certificate.dateOfCompletion) : new Date();

        const params = {
          startTask: 'CERTIFICATION_NAME',
          organizationId: data.certification?.linkedInSharing?.linkedInOrganizationId,
          organizationName: data.certification?.linkedInSharing?.organizationName,
          name: certificate.customCertificate.name,
          issueYear: dateOfCompletion.getFullYear(),
          issueMonth: dateOfCompletion.getMonth() + 1,
          certId: token?.slice(0, 20),
          certUrl: certificateUrl
        };

        Object.entries(params).forEach(([key, value]) => {
          if (!value) {
            return;
          }

          url.searchParams.append(key, value.toString());
        });


        window.open(url, '_blank');
      });
  }

  public previewCertificate(certificate: UserCertificate): void {
    this.dialog.open(CertificatePreviewModalComponent, {
      disableClose: false,
      closeOnNavigation: true,
      panelClass: ['mobile-full-width-dialog', 'dialog-no-padding', 'dialog-no-margin'],
      data: { ...certificate }
    });
  }
}
